import {
  type AttributionContext,
  type PartnersBlock,
  CLICK_ID_DEFINITIONS,
  captureIntoContext,
  parseCookieHeader,
} from "@nextday-ai/common-ts-model";

import { getAttributionHost } from "@attribution/host";
import { readAttributionContext, writeAttributionContext } from "@attribution/storage";

/**
 * Reads `?ref=` or `?aff_id=` from the landing URL and returns a
 * `PartnersBlock` patch so the affiliate referral code is persisted in the
 * attribution envelope. Returns `undefined` when neither param is present.
 */
const readAffiliateIdFromUrl = (params: URLSearchParams): PartnersBlock | undefined => {
  const affiliateId = params.get("ref") || params.get("aff_id") || undefined;
  if (affiliateId === undefined || affiliateId === "") return undefined;
  return { affiliateId };
};

const UTM_PARAMS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"] as const;

/**
 * Returns true when `params` already carries at least one UTM, recognized
 * click-id, or `network` parameter — i.e. the URL already has enough
 * acquisition signal for `isRecognizedAcquisitionHit` to fire.
 *
 * Click-id params are derived from `CLICK_ID_DEFINITIONS` so this stays in
 * sync with the shared attribution model automatically.
 */
const hasExplicitAcquisitionParams = (params: URLSearchParams): boolean =>
  UTM_PARAMS.some((p) => params.has(p)) ||
  CLICK_ID_DEFINITIONS.some((def) => params.has(def.urlParam)) ||
  params.has("network");

/**
 * When `?ref=` (or `?aff_id=`) is the only acquisition signal on the URL,
 * returns a copy of `params` with `utm_source=affiliate` injected so the
 * downstream capture reducer treats it as a recognized hit. When the URL
 * already carries UTM / click-id / network params, returns the original
 * `params` unchanged.
 */
const withImpliedAffiliateUtm = (
  params: URLSearchParams,
  hasAffiliate: boolean,
): URLSearchParams => {
  if (!hasAffiliate || hasExplicitAcquisitionParams(params)) return params;
  const patched = new URLSearchParams(params);
  patched.set("utm_source", "affiliate");
  return patched;
};

/**
 * Runs the pure-core capture reducer against the live browser state
 * (`window.location`, `document.referrer`, `document.cookie`), merges the
 * result with whatever envelope is already persisted, and writes the merged
 * envelope back. No-op on SSR.
 */
export const captureFromCurrentWindow = (): AttributionContext | undefined => {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return undefined;
  }

  const host = getAttributionHost();
  const previous = readAttributionContext();
  const url = new URL(window.location.href);
  const affiliatePartners = readAffiliateIdFromUrl(url.searchParams);
  const effectiveParams = withImpliedAffiliateUtm(
    url.searchParams,
    affiliatePartners !== undefined,
  );

  const { next } = captureIntoContext(previous, {
    params: effectiveParams,
    landingUrl: window.location.href,
    referrer: document.referrer,
    cookies: parseCookieHeader(document.cookie),
    now: host.now(),
    anonymousId: previous?.anonymousId ?? host.generateAnonymousId(),
    legacyPartners: affiliatePartners,
  });

  writeAttributionContext(next);
  return next;
};
